import React from 'react';
import { Gantt } from '@svar-ui/react-gantt';
import '@svar-ui/react-gantt/style.css';
import { format } from 'date-fns';
import { Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useContracts } from '../services/contractService';
import { parseCalendarDate } from '../utils/calendarDate';

/** Local midnight of a date-only value's calendar day, in ms (NaN when it has none). */
const calendarTime = (iso: string): number => parseCalendarDate(iso)?.getTime() ?? Number.NaN;

const STATUS_COLORS: Record<string, string> = {
  Active: '#22C55E',
  PendingRenewal: '#FFAB00',
  Expired: '#FF5630',
  Draft: '#919EAB',
  Terminated: '#637381',
};

const taskTypes = [
  { id: 'task', label: 'Task' },
  { id: 'summary', label: 'Summary' },
  { id: 'Active', label: 'Active' },
  { id: 'PendingRenewal', label: 'Pending Renewal' },
  { id: 'Expired', label: 'Expired' },
  { id: 'Draft', label: 'Draft' },
  { id: 'Terminated', label: 'Terminated' },
];

const Timeline: React.FC = () => {

  const { data: contractsData, isLoading, error } = useContracts({
    pageSize: 200,
    pageNumber: 1,
  });

  // Filter out E2E test-created contracts
  const contracts = (contractsData?.items || []).filter(
    c => !c.title.startsWith('E2E ') && !c.contractNumber.startsWith('E2E-')
  );

  // Map contracts to SVAR Gantt task format
  const tasks = React.useMemo(() => {
    if (!contracts.length) return [];

    // Group by vendor for parent/child structure
    const vendorGroups = new Map<string, typeof contracts>();
    for (const c of contracts) {
      const vendor = c.vendorName || 'Unknown';
      if (!vendorGroups.has(vendor)) vendorGroups.set(vendor, []);
      vendorGroups.get(vendor)!.push(c);
    }

    const ganttTasks: Array<{
      id: string | number;
      text: string;
      start: Date;
      end: Date;
      progress: number;
      parent?: number;
      type: string;
      open?: boolean;
    }> = [];

    let parentId = 10000;
    for (const [vendor, vendorContracts] of vendorGroups) {
      const pid = parentId++;
      // Find the earliest start and latest end for the vendor group
      // Calendar days at local midnight (#65), so a bar starts on the stored day in every time zone
      const starts = vendorContracts.map(c => calendarTime(c.startDate));
      const ends = vendorContracts.map(c => calendarTime(c.endDate));

      ganttTasks.push({
        id: pid,
        text: vendor,
        start: new Date(Math.min(...starts)),
        end: new Date(Math.max(...ends)),
        progress: 0,
        type: 'summary',
        open: true,
      });

      for (const c of vendorContracts) {
        const start = new Date(calendarTime(c.startDate));
        const end = new Date(calendarTime(c.endDate));
        const now = Date.now();
        const total = end.getTime() - start.getTime();
        const elapsed = Math.max(0, now - start.getTime());
        const progress = total > 0 ? Math.min(100, Math.round((elapsed / total) * 100)) : 0;

        ganttTasks.push({
          id: c.id,
          text: `${c.title} (${c.contractNumber})`,
          start,
          end,
          progress,
          parent: pid,
          type: c.status || 'Active',
        });
      }
    }

    return ganttTasks;
  }, [contracts]);

  const scales = [
    { unit: 'year' as const, step: 1, format: (date: Date) => format(date, 'yyyy') },
    { unit: 'quarter' as const, step: 1, format: (date: Date) => 'Q' + (Math.floor(date.getMonth() / 3) + 1) },
  ];

  const columns = [
    { id: 'text', header: 'Contract / Vendor', flexgrow: 1 },
  ];

  if (isLoading) {
    return (
      <div className="max-w-screen-xl mx-auto px-4 py-8">
        <div className="flex justify-center items-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-screen-xl mx-auto px-4 py-8">
        <Alert variant="destructive">
          <AlertDescription>Failed to load contracts for timeline. Please try again later.</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="max-w-screen-xl mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-3xl font-semibold text-grey-800">Contract Timeline</h1>
        <p className="text-sm text-grey-500 mt-1">
          Gantt-style view of all contracts grouped by vendor. Progress shows elapsed contract duration.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Contract Schedule</CardTitle>
          <CardDescription>
            {contracts.length} contracts across {new Set(contracts.map(c => c.vendorName)).size} vendors
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {tasks.length > 0 ? (
            <div style={{ height: Math.min(700, Math.max(400, tasks.length * 36 + 100)) }}>
              <Gantt
                tasks={tasks}
                scales={scales}
                columns={columns}
                taskTypes={taskTypes}
                cellWidth={80}
                cellHeight={36}
                zoom={true}
                readonly={true}
              />
            </div>
          ) : (
            <div className="py-12 text-center">
              <p className="text-grey-500">No contracts to display</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Status bar color styles */}
      <style>{`
        .wx-gantt .wx-bar.Active {
          background-color: #22C55E !important;
          border-color: #16a34a !important;
        }
        .wx-gantt .wx-bar.Active .wx-progress-percent {
          background-color: #16a34a !important;
        }
        .wx-gantt .wx-bar.PendingRenewal {
          background-color: #FFAB00 !important;
          border-color: #e69500 !important;
        }
        .wx-gantt .wx-bar.PendingRenewal .wx-progress-percent {
          background-color: #e69500 !important;
        }
        .wx-gantt .wx-bar.Expired {
          background-color: #FF5630 !important;
          border-color: #de350b !important;
        }
        .wx-gantt .wx-bar.Expired .wx-progress-percent {
          background-color: #de350b !important;
        }
        .wx-gantt .wx-bar.Draft {
          background-color: #919EAB !important;
          border-color: #78868f !important;
        }
        .wx-gantt .wx-bar.Draft .wx-progress-percent {
          background-color: #78868f !important;
        }
        .wx-gantt .wx-bar.Terminated {
          background-color: #637381 !important;
          border-color: #4b5563 !important;
        }
        .wx-gantt .wx-bar.Terminated .wx-progress-percent {
          background-color: #4b5563 !important;
        }
        .wx-gantt .wx-bar.Active,
        .wx-gantt .wx-bar.PendingRenewal,
        .wx-gantt .wx-bar.Expired,
        .wx-gantt .wx-bar.Draft,
        .wx-gantt .wx-bar.Terminated {
          color: #fff !important;
        }
      `}</style>

      {/* Legend */}
      <div className="mt-4 flex flex-wrap gap-4">
        {Object.entries(STATUS_COLORS).map(([status, color]) => (
          <div key={status} className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: color }} />
            <span className="text-sm text-grey-500">{status}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Timeline;
